import { Directionality } from '@angular/cdk/bidi';
import { coerceArray, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ESCAPE, UP_ARROW } from '@angular/cdk/keycodes';
import {
  Overlay,
  OverlayConfig,
  OverlayRef,
  PositionStrategy,
  ScrollStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EmbeddedViewRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ThemePalette } from '@angular/material/core';
import { AutoUnsubscribe } from '@nitro/core/common';
import { merge, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { NkPickerInput } from './picker-input';

let pickerUid = 0;

/** Injection token that determines the scroll handling while the calendar is open. */
export const NK_PICKER_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>(
  'NK_PICKER_SCROLL_STRATEGY'
);

/** @docs-private */
export function NK_PICKER_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

/** @docs-private */
export const NK_PICKER_SCROLL_STRATEGY_FACTORY_PROVIDER = {
  provide: NK_PICKER_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: NK_PICKER_SCROLL_STRATEGY_FACTORY
};

@AutoUnsubscribe()
@Component({
  selector: 'nk-picker',
  template: '<ng-template><ng-content></ng-content></ng-template>',
  exportAs: 'nkPicker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NkPicker<V> implements OnDestroy, AfterContentInit {
  private _scrollStrategy: () => ScrollStrategy;

  // @ts-ignore
  @ViewChild(TemplateRef) _template;
  // @ts-ignore
  @ContentChild('nkPickerControl', { static: true }) pickerControl;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _value: any;

  // This is required because MatDatepickerBase class implements it starting from angular 9
  // TODO: (MATERIAL 10) Create custom mat-datepicker-toggle for nitro
  /** Emits when the datepicker's state changes. */
  readonly _stateChanges = new Subject<void>();

  /** Color palette to use on the datepicker's calendar. */
  @Input()
  get color(): ThemePalette {
    return this._color || (this._pickerInput ? this._pickerInput._getThemePalette() : undefined);
  }
  set color(value: ThemePalette) {
    this._color = value;
  }
  _color: ThemePalette;

  /**
   * Whether the calendar UI is in touch mode. In touch mode the calendar opens in a dialog rather
   * than a popup and elements have more padding to allow for bigger touch targets.
   */
  @Input()
  get touchUi(): boolean {
    return this._touchUi;
  }
  set touchUi(value: boolean) {
    this._touchUi = coerceBooleanProperty(value);
  }
  private _touchUi = false;

  @Input()
  get disabled(): boolean {
    if (this._pickerInput) {
      return typeof this._pickerInput.disabled === 'boolean'
        ? this._pickerInput.disabled || this._disabled
        : this._disabled;
    } else {
      return this.disabled;
    }
  }
  set disabled(value: boolean) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this._disabled) {
      this._disabledChange.next(newValue);
    }
    this._disabled = newValue;
  }
  private _disabled: boolean;

  /** Classes to be passed to the date picker panel. Supports the same syntax as `ngClass`. */
  @Input() panelClass: string | string[];

  /** Emits when the datepicker has been opened. */
  // eslint-disable-next-line
  @Output('opened')
  openedStream: EventEmitter<V | void> = new EventEmitter<V | void>();

  /** Emits when the datepicker has been closed. */
  // eslint-disable-next-line
  @Output('closed')
  closedStream: EventEmitter<V | void> = new EventEmitter<V | void>();

  /** Whether the calendar is open. */
  @Input()
  get opened(): boolean {
    return this._opened;
  }
  set opened(value: boolean) {
    value ? this.open() : this.close();
  }
  private _opened = false;

  /** The id for the picker. */
  id = `nk-picker-${pickerUid++}`;

  /** A reference to the overlay when the calendar is opened as a popup. */
  private _popupRef: OverlayRef;

  /** A reference to the dialog when the calendar is opened as a dialog. */
  private _dialogRef: MatDialogRef<any> | null;

  /** A portal containing the calendar for this datepicker. */
  private _templatePortal: TemplatePortal<any>;

  /** Reference to the component instantiated in popup mode. */
  private _popupComponentRef: EmbeddedViewRef<any> | null;

  /** The element that was focused before the datepicker was opened. */
  private _focusedElementBeforeOpen: HTMLElement | null = null;

  /** The input element this datepicker is associated with. */
  _pickerInput: NkPickerInput<V>;

  /** Emits when the datepicker is disabled. */
  readonly _disabledChange = new Subject<boolean>();

  /** Emits new selected date when selected date changes. */
  // readonly _selectedChanged = new Subject<V>();

  constructor(
    private _dialog: MatDialog,
    private _overlay: Overlay,
    private _ngZone: NgZone,
    private _viewContainerRef: ViewContainerRef,

    @Optional() @Inject(NK_PICKER_SCROLL_STRATEGY) private scrollStrategy: any,
    @Optional() private _dir: Directionality,
    @Optional() @Inject(DOCUMENT) private _document: any,
    private injector: Injector
  ) {
    this._scrollStrategy =
      this.scrollStrategy ||
      Injector.create({
        providers: [NK_PICKER_SCROLL_STRATEGY_FACTORY_PROVIDER],
        parent: this.injector
      }).get(NK_PICKER_SCROLL_STRATEGY);
  }

  ngOnDestroy() {
    this.close();
    this._disabledChange.complete();

    if (this._popupRef) {
      this._popupRef.dispose();
      this._popupComponentRef = null;
    }
  }
  /**
   * Register an input with this datepicker.
   * @param input The datepicker input to register with this datepicker.
   */
  _registerInput(input: NkPickerInput<V>): void {
    if (this._pickerInput) {
      throw Error('A NkPicker can only be associated with a single input.');
    }
    this._pickerInput = input;
  }

  /** Open the picker control. */
  open(): void {
    if (this._opened || this.disabled) {
      return;
    }
    if (!this._pickerInput) {
      throw Error('Attempted to open an nkPicker with no associated input.');
    }

    if (this._document) {
      this._focusedElementBeforeOpen = this._document.activeElement;
    }
    this.pickerControl.value = this._pickerInput.value;
    this.pickerControl.updateControls(this._pickerInput.value);

    this.touchUi ? this._openAsDialog() : this._openAsPopup();
    this._opened = true;
    this.openedStream.emit();
  }

  /** Close the picker control. */
  close(cancelled: boolean = false): void {
    if (!this._opened) {
      return;
    }
    if (this._popupRef && this._popupRef.hasAttached()) {
      this._popupRef.removePanelClass(`mat-${this.color}`);
      this._popupRef.detach();
    }
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }
    if (this._templatePortal && this._templatePortal.isAttached) {
      this._templatePortal.detach();
    }

    const completeClose = (_cancel: boolean) => {
      // The `_opened` could've been reset already if
      // we got two events in quick succession.
      if (this._opened) {
        // this._selectedChanged.next(this.pickerControl.value);
        this._opened = false;
        if (!this.closedStream.isStopped) {
          this.closedStream.emit(!_cancel ? this.pickerControl.value : void 0);
        }
        this._focusedElementBeforeOpen = null;
      }
    };

    if (
      this._focusedElementBeforeOpen &&
      typeof this._focusedElementBeforeOpen.focus === 'function'
    ) {
      // Because IE moves focus asynchronously, we can't count on it being restored before we've
      // marked the datepicker as closed. If the event fires out of sequence and the element that
      // we're refocusing opens the datepicker on focus, the user could be stuck with not being
      // able to close the calendar at all. We work around it by making the logic, that marks
      // the datepicker as closed, async as well.
      this._focusedElementBeforeOpen.focus();
      setTimeout(() => completeClose(cancelled));
    } else {
      completeClose(cancelled);
    }
  }

  cancel(): void {
    this.close(true);
  }

  /** Open the calendar as a dialog. */
  private _openAsDialog(): void {
    // Usually this would be handled by `open` which ensures that we can only have one overlay
    // open at a time, however since we reset the variables in async handlers some overlays
    // may slip through if the user opens and closes multiple times in quick succession (e.g.
    // by holding down the enter key).

    if (this._dialogRef) {
      this._dialogRef.close();
    }

    this._dialogRef = this._dialog.open(this._template, {
      direction: this._dir ? this._dir.value : 'ltr',
      viewContainerRef: this._viewContainerRef,
      panelClass: [
        'mat-datepicker-content',
        'nk-picker-content',
        ...coerceArray(this.panelClass),
        `mat-${this.color}`
      ]
    });

    this._dialogRef.afterClosed().subscribe(() => this.close());
  }

  /** Open the calendar as a popup. */
  private _openAsPopup(): void {
    if (!this._templatePortal) {
      this._templatePortal = new TemplatePortal<any>(this._template, this._viewContainerRef);
    }

    if (!this._popupRef) {
      this._createPopup();
    }

    if (!this._popupRef.hasAttached()) {
      this._popupComponentRef = this._popupRef.attach(this._templatePortal);

      // Update the position once the picker control has rendered.
      this._ngZone.onStable
        .asObservable()
        .pipe(take(1))
        .subscribe(() => {
          this._popupRef.updatePosition();
        });
      this._popupRef.addPanelClass(`mat-${this.color}`);
    }
  }

  /** Create the popup. */
  private _createPopup(): void {
    const overlayConfig = new OverlayConfig({
      positionStrategy: this._createPopupPositionStrategy(),
      hasBackdrop: true,
      backdropClass: 'mat-overlay-transparent-backdrop',
      direction: this._dir,
      scrollStrategy: this._scrollStrategy(),
      panelClass: [
        'mat-datepicker-popup',
        'nk-picker-content',
        'mat-datepicker-content',
        ...coerceArray(this.panelClass)
      ]
    });

    this._popupRef = this._overlay.create(overlayConfig);
    this._popupRef.overlayElement.setAttribute('role', 'dialog');

    merge(this._popupRef.backdropClick()).subscribe(event => {
      if (event) {
        event.preventDefault();
      }
      this.close();
    });

    this._popupRef
      .keydownEvents()
      .pipe(
        filter(event => {
          // Closing on alt + up is only valid when there's an input associated with the datepicker.
          return (
            event.keyCode === ESCAPE ||
            (this._pickerInput && event.altKey && event.keyCode === UP_ARROW)
          );
        })
      )
      .subscribe(event => {
        if (event) {
          event.preventDefault();
        }
        this.close(true);
      });
  }

  /** Create the popup PositionStrategy. */
  private _createPopupPositionStrategy(): PositionStrategy {
    return this._overlay
      .position()
      .flexibleConnectedTo(this._pickerInput.getConnectedOverlayOrigin())
      .withTransformOriginOn('.mat-datepicker-content')
      .withFlexibleDimensions(false)
      .withViewportMargin(8)
      .withLockedPosition()
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
      ]);
  }
  ngAfterContentInit() {
    // this.panelClass = this.panelClass || this.pickerControl.panelClass ;
    if (!this.pickerControl) {
      throw new Error(
        'Picker control misssing. Did you set the template variable #nkPickerControl ?'
      );
    }
  }
}
